import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import boTheme from '@ecp/common/src/style/theme/bo';
import { zIndex } from '@ecp/common/src/style/constant';
import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import Modal from '@ecp/common/src/components/modal/bo/Modal';
import { placements } from '@ecp/common/src/components/unstyled/floating/useModal';
import { Button } from '@ecp/common/src/components/button/bo/Button';
import { ReactComponent as AlertIcon } from '@ecp/common/src/assets/icon/icon__bo__alert.svg';

const MessageBox = styled.div`
  width: 100%;
  height: fit-content;
  flex: 1;
  font-weight: ${boTheme.font.weight.regular};
  font-size: ${boTheme.font.size.basic};
  line-height: 130%;
  white-space: pre-wrap;
  word-break: keep-all;
  text-align: center;
  color: ${({ color }) => color};
`;

const DefaultAlertIcon = styled(AlertIcon)`
  margin-right: 6px;
`;

export const StructurePaper = ({ width, title, icon: Icon, content, color, bottom }) => {
  return (
    <FlexBoxColumn width={width} gap="24px" padding={'16px 0 0 0'} style={{ marginTop: '16px' }}>
      {title && (
        <FlexBox height="32px">
          {title}
          <button></button>
        </FlexBox>
      )}
      <FlexBox align-items={'flex-center'}>
        {Icon && <Icon />}
        <MessageBox color={color}>{content}</MessageBox>
      </FlexBox>
      <FlexBox justify-content="center" gap="10px">
        {bottom}
      </FlexBox>
    </FlexBoxColumn>
  );
};

StructurePaper.displayName = 'StructurePaper';

StructurePaper.propTypes = {
  width: PropTypes.string,
  title: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element, PropTypes.node]),
  content: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  color: PropTypes.string,
  bottom: PropTypes.node,
  onClose: PropTypes.func,
};

export const AlertDialog = ({ message, children, open, onClose, severity = 'alert', confirmButtonText = '확인' }) => {
  const handleCloseClick = () => {
    onClose?.();
  };

  const bottomComponent = () => {
    return (
      <Button width="90px" onClick={handleCloseClick}>
        {confirmButtonText}
      </Button>
    );
  };

  const AlertComponent = ({ onClose }) => {
    const handleClose = () => {
      onClose?.();
    };

    const severityStyleHandler = () => {
      if ('alert' === severity) {
        return { icon: DefaultAlertIcon, color: boTheme.color.error1 };
      }
      return { color: boTheme.color.sub1 };
    };

    return (
      <StructurePaper
        content={message}
        color={boTheme.color.error1}
        bottom={bottomComponent({ onClose: handleClose })}
        {...severityStyleHandler()}
      />
    );
  };

  return (
    <Modal
      closeable
      innerModal
      placement={'center'}
      width={'320px'}
      closeWhenOutside={false}
      zIndex={zIndex.dialog}
      value={AlertComponent}
      open={open}
      onClose={onClose}
    >
      {children}
    </Modal>
  );
};

AlertDialog.propTypes = {
  severity: PropTypes.oneOf(['none', 'alert']),
  message: PropTypes.string,
  children: PropTypes.node,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  confirmButtonText: PropTypes.string,
  placement: PropTypes.oneOf(placements),
};

export const ConfirmDialog = ({
  message,
  children,
  open,
  onConfirm,
  onClose,
  confirmButtonText = '확인',
  closeButtonText = '취소',
}) => {
  const bottomComponent = () => {
    return (
      <>
        <Button type={'Third'} width={'90px'} onClick={onClose}>
          {closeButtonText}
        </Button>
        <Button width={'90px'} onClick={onConfirm}>
          {confirmButtonText}
        </Button>
      </>
    );
  };

  const ConfirmComponent = ({ onClose }) => {
    const handleClose = () => {
      onClose?.();
    };
    return (
      <StructurePaper content={message} color={boTheme.color.sub1} bottom={bottomComponent({ onClose: handleClose })} />
    );
  };

  return (
    <Modal
      closeable
      innerModal
      placement={'center'}
      width={'320px'}
      closeWhenOutside={false}
      zIndex={zIndex.dialog}
      value={ConfirmComponent}
      open={open}
      onClose={onClose}
    >
      {children}
    </Modal>
  );
};

ConfirmDialog.propTypes = {
  message: PropTypes.string,
  children: PropTypes.node,
  open: PropTypes.bool,
  onConfirm: PropTypes.func,
  onClose: PropTypes.func,
  placement: PropTypes.oneOf(placements),
  confirmButtonText: PropTypes.string,
  closeButtonText: PropTypes.string,
};
